/**
 * Client-side password gate for exported HTML (TN exports, and GCC exports since WI 2506).
 *
 * The gate is an overlay: the file still holds the dashboard, and the overlay is removed
 * once the PBKDF2 hash of the typed password matches the embedded one. It keeps a
 * recipient from casually opening the file; it is not encryption.
 */

/** PBKDF2 settings shared by every gate: the exported script must derive the same bits. */
const PBKDF2_ITERATIONS = 100000
const HASH_BYTES = 32
const SALT_BYTES = 16

/** Wording on the gate. The defaults are the TN navigator's. */
interface PasswordGateLabels {
  logo: string
  title: string
  subtitle: string
  button: string
}

const TN_GATE_LABELS: PasswordGateLabels = {
  logo: 'TN',
  title: 'Navigator Protected',
  subtitle: 'Enter the password to view this navigator',
  button: 'Unlock Navigator',
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;')
}

function toHex(bytes: Uint8Array): string {
  return Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('')
}

/**
 * Inject a client-side password gate into an HTML string.
 * Uses PBKDF2 with a random salt for secure password verification.
 *
 * Uses the Web Crypto API — no external dependencies needed in the exported file.
 */
export function injectPasswordGate(
  html: string,
  passwordHash: string,
  salt: string,
  labels: PasswordGateLabels = TN_GATE_LABELS,
): string {
  const l = {
    logo: escapeHtml(labels.logo),
    title: escapeHtml(labels.title),
    subtitle: escapeHtml(labels.subtitle),
    button: escapeHtml(labels.button),
  }
  const gate = `
<style id="pw-gate-style">
  #pw-gate-overlay{position:fixed;inset:0;z-index:99999;background:#1a1a2e;display:flex;align-items:center;justify-content:center;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif}
  #pw-gate-box{background:#fff;border-radius:16px;padding:40px 48px;width:380px;text-align:center;box-shadow:0 20px 60px rgba(0,0,0,.5)}
  #pw-gate-logo{font-size:28px;margin-bottom:8px}
  #pw-gate-title{font-size:20px;font-weight:700;color:#1e293b;margin-bottom:6px}
  #pw-gate-sub{font-size:13px;color:#64748b;margin-bottom:24px}
  #pw-gate-input{width:100%;box-sizing:border-box;padding:12px 16px;border:2px solid #e2e8f0;border-radius:8px;font-size:15px;outline:none;transition:border-color .2s;margin-bottom:16px}
  #pw-gate-input:focus{border-color:var(--accenture-purple, #A100FF)}
  #pw-gate-btn{width:100%;padding:13px;background:linear-gradient(135deg, var(--accenture-purple, #A100FF), var(--accenture-purple-dark, #7900B8));color:#fff;border:none;border-radius:8px;font-size:15px;font-weight:700;cursor:pointer;transition:opacity .2s}
  #pw-gate-btn:hover{opacity:.9}
  #pw-gate-err{font-size:13px;color:#dc2626;margin-top:12px;min-height:18px}
</style>
<div id="pw-gate-overlay">
  <div id="pw-gate-box">
    <div id="pw-gate-logo">${l.logo}</div>
    <div id="pw-gate-title">${l.title}</div>
    <div id="pw-gate-sub">${l.subtitle}</div>
    <input id="pw-gate-input" type="password" placeholder="Enter password…" autocomplete="current-password"/>
    <button id="pw-gate-btn">${l.button}</button>
    <div id="pw-gate-err"></div>
  </div>
</div>
<script>
(function(){
  var HASH = '${passwordHash}';
  var SALT = '${salt}';
  async function pbkdf2(str, saltHex) {
    var enc = new TextEncoder();
    var saltBytes = new Uint8Array(saltHex.match(/.{2}/g).map(function(b){return parseInt(b,16)}));
    var keyMaterial = await crypto.subtle.importKey('raw', enc.encode(str), 'PBKDF2', false, ['deriveBits']);
    var bits = await crypto.subtle.deriveBits({name:'PBKDF2', salt:saltBytes, iterations:${PBKDF2_ITERATIONS}, hash:'SHA-256'}, keyMaterial, ${HASH_BYTES * 8});
    return Array.from(new Uint8Array(bits)).map(function(b){return b.toString(16).padStart(2,'0')}).join('');
  }
  function unlock() {
    var val = document.getElementById('pw-gate-input').value;
    if (!val) return;
    pbkdf2(val, SALT).then(function(h) {
      if (h === HASH) {
        document.getElementById('pw-gate-overlay').remove();
        document.getElementById('pw-gate-style').remove();
      } else {
        document.getElementById('pw-gate-err').textContent = 'Incorrect password. Please try again.';
        document.getElementById('pw-gate-input').value = '';
        document.getElementById('pw-gate-input').focus();
      }
    });
  }
  document.getElementById('pw-gate-btn').addEventListener('click', unlock);
  document.getElementById('pw-gate-input').addEventListener('keydown', function(e){ if(e.key==='Enter') unlock(); });
})();
</script>`

  return html.replace('</body>', gate + '\n</body>')
}

/** True when this runtime has Web Crypto (browsers only expose it in a secure context: https, localhost or file://). */
export function hasWebCrypto(): boolean {
  return typeof globalThis.crypto?.subtle?.deriveBits === 'function'
}

/** PBKDF2-SHA-256 of a password with a hex salt, as hex: the same bits the gate script derives. */
export async function pbkdf2Hex(password: string, saltHex: string): Promise<string> {
  const saltBytes = new Uint8Array((saltHex.match(/.{2}/g) ?? []).map(b => parseInt(b, 16)))
  const keyMaterial = await crypto.subtle.importKey('raw', new TextEncoder().encode(password), 'PBKDF2', false, ['deriveBits'])
  const bits = await crypto.subtle.deriveBits(
    { name: 'PBKDF2', salt: saltBytes, iterations: PBKDF2_ITERATIONS, hash: 'SHA-256' },
    keyMaterial,
    HASH_BYTES * 8,
  )
  return toHex(new Uint8Array(bits))
}

/**
 * Hash a password for the gate with Web Crypto and a random salt, for code that runs in
 * the browser (the GCC export). Same settings as core's Node hashPassword.
 */
export async function hashPasswordWebCrypto(password: string): Promise<{ hash: string; salt: string }> {
  if (!hasWebCrypto()) throw new Error('Password protection needs a secure (https) connection in this browser.')
  const salt = toHex(crypto.getRandomValues(new Uint8Array(SALT_BYTES)))
  return { hash: await pbkdf2Hex(password, salt), salt }
}
